import React, {
    useRef,
    ReactNode,
    useEffect,
    MutableRefObject,
    forwardRef
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { AnimatePresence, motion } from 'framer-motion';
import { applyOpacity } from '../../utils/color.utils';
import { MODAL_ANIMATION } from '../HBModal/HBModal.styles';
import { guid } from '../../utils/shared.utils';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { ModalInternalProvider } from './ModalInternalContext';
import { useHBModalContext } from '../HBModalProvider/HBModalProvider';
import { Z_INDEX } from '../../styles/variables';

export interface ModalControlProps {
    isOpen: boolean;
    onClose: () => void;
    /** When false, clicking outside the modal content does not trigger onClose. */
    closeOnOutsideClick?: boolean;
}

export interface Props extends ModalControlProps {
    children?: ReactNode;
}

const HBModalBase = (
    {
        children,
        testIdName,
        isOpen,
        onClose,
        closeOnOutsideClick = true,
        ...props
    }: Props & MustIncludeOneOfBasicTestIdWithIdProps,
    ref: MutableRefObject<HTMLDivElement>
) => {
    const _containerRef = useRef<HTMLDivElement>(null);
    const containerRef = ref ?? _containerRef;

    const { current: internalStableId } = useRef(guid());
    const id = props.id || internalStableId;
    const idRef = useRef(id);
    idRef.current = id;

    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const [modals, setModals] = useHBModalContext();

    useEffect(() => {
        function removeModal() {
            setModals(prevModals =>
                prevModals.filter(modal => modal.idRef.current !== id)
            );
        }

        if (isOpen) {
            setModals(prevModals => [
                ...prevModals,
                {
                    idRef,
                    containerRef,
                    onCloseRef
                }
            ]);
            return removeModal;
        } else {
            removeModal();
        }
    }, [isOpen, id, setModals, containerRef]);

    const isBottomModal = modals[0]?.idRef.current === id;

    const portalTarget = (() => {
        if (isBottomModal) {
            return document.body;
        }
        const index = modals.findIndex(modal => modal.idRef.current === id);
        const parentIndex = index - 1;
        const parentModal = modals[parentIndex]?.containerRef.current;
        const lastModal = modals[modals.length - 1]?.containerRef.current;
        return parentModal || lastModal || document.body;
    })();

    const testId = props.testId ?? props.id;
    const containerTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'container',
        componentName: 'HBModal'
    });

    return (
        <ModalInternalProvider value={{ testIdName, testId }}>
            <Dialog.Root
                open={isOpen}
                onOpenChange={newIsOpen => {
                    if (!newIsOpen) {
                        onClose();
                    }
                }}
            >
                <Dialog.Portal forceMount container={portalTarget}>
                    <AnimatePresence>
                        {isOpen && (
                            <Overlay
                                {...MODAL_ANIMATION}
                                $isBottomModal={isBottomModal}
                            >
                                <Container
                                    id={props.id}
                                    data-testid={containerTestId}
                                    ref={containerRef}
                                    $isBottomModal={isBottomModal}
                                    {...(!closeOnOutsideClick && {
                                        onInteractOutside: e =>
                                            e.preventDefault()
                                    })}
                                >
                                    {children}
                                </Container>
                            </Overlay>
                        )}
                    </AnimatePresence>
                </Dialog.Portal>
            </Dialog.Root>
        </ModalInternalProvider>
    );
};

export default forwardRef(HBModalBase);

// -------------------------- Styles -------------------------- //

type StyleProps = { $isBottomModal: boolean };

const borderRadius = css`
    border-radius: ${vars.radius.sm};
`;

const Overlay = styled(motion(Dialog.Overlay))<StyleProps>`
    inset: 0;
    display: grid;
    place-content: center;
    z-index: ${Z_INDEX.POPOVER};
    ${({ $isBottomModal }) =>
        $isBottomModal
            ? css`
                  padding: 16px;
                  position: fixed;
                  background-color: ${applyOpacity(
                      vars.colors.surface.containerInverted,
                      70
                  )};
              `
            : css`
                  position: absolute;
                  background-color: ${applyOpacity(
                      vars.colors.surface.container,
                      85
                  )};
                  ${borderRadius}
              `}
`;

const Container = styled(Dialog.Content)<StyleProps>`
    position: relative;
    background-color: ${vars.colors.surface.container};
    outline: none;
    &,
    > *:only-child {
        ${borderRadius}
    }

    ${({ $isBottomModal }) =>
        !$isBottomModal &&
        css`
            box-shadow: ${vars.shadow['50']};
        `}
`;
